/**
 * The shape a faceted filter takes, shared by the rail that sets it and the sentence that
 * explains it.
 *
 * Both components read this one contract deliberately. A rail can only ever show WHICH boxes are
 * ticked; it cannot show what the ticks mean together - that values inside one facet are OR'd
 * while separate facets are AND'ed is invisible in any arrangement of checkboxes. The sentence
 * exists to state that relationship in words, and it can only stay true to the rail if both are
 * derived from the same declaration rather than written twice.
 */

/** One tickable value within a facet. */
export interface FilterFacetOption {
  /** The canonical key, never the word on screen - labels are a display concern that changes. */
  value: string;
  /** What the person reads. */
  label: string;
  /**
   * How many rows this option would show, counted with this facet's OWN selection ignored.
   *
   * The exclusion is what stops the count reading as broken: counted naively, ticking "Member"
   * would render every sibling in the same facet as `0`, because no row can be both. Optional -
   * a host with no cheap way to count omits it and no count is rendered.
   */
  count?: number;
}

/** One group of independently tickable values. */
export interface FilterFacet {
  /** Stable key; also the key this facet's selection is stored under. */
  key: string;
  /** The group heading in the rail, e.g. "Membership status". */
  title: string;
  options: FilterFacetOption[];
  /**
   * The lead-in words this facet contributes to the query sentence, e.g. `'whose membership is'`
   * produces "whose membership is Member or Former member".
   *
   * Written as a fragment rather than a whole clause so the sentence reads as English instead of
   * a list of key/value pairs - "showing companies that are Active, whose membership is Member"
   * rather than "state: Active, membership: Member".
   */
  phrase: string;
  /**
   * What the rail says when this facet has no options to offer, e.g. "Choose a register to
   * filter by series." A facet whose values depend on another choice still renders - the rail
   * keeps its shape (render the destination shape, not the current minimum) - and this is the
   * one line that explains why it is empty.
   */
  emptyHint?: string;
  /**
   * This facet's values are data rather than a catalogue and grow without bound - series /
   * range "could potentially have dozens or hundreds of items for a supplier" (architect,
   * 2026-09-06). The rail caps its list, scrolls it inside itself and gives it a narrowing
   * box, however few values it holds right now: the box is part of the facet's shape. A
   * catalogue facet leaves this unset and shows every value.
   */
  narrowable?: boolean;
}

/**
 * Which values are ticked, keyed by facet key. A facet with no entry, or an empty array, is not
 * narrowing anything.
 *
 * Nothing ticked meaning "everything" is what removes the "All" option the old radio group needed:
 * with independent checkboxes the empty set already says it, and an explicit All would be a fourth
 * control that can disagree with the other three.
 */
export type FilterSelection = Record<string, string[]>;

/** One condition of the query, as the sentence will render it. */
export interface FilterClause {
  /** The facet this clause came from, so removing it can clear exactly that facet. */
  key: string;
  /** The lead-in, e.g. "whose membership is". */
  phrase: string;
  /** The chosen labels, in the facet's own declared order rather than click order. */
  labels: string[];
}

/** True when nothing in the selection is narrowing the list. */
export function isSelectionEmpty(selection: FilterSelection): boolean {
  return Object.values(selection).every(
    (values) => !values || values.length === 0
  );
}

/** The values ticked for one facet, tolerating a facet the selection has never seen. */
export function selectedValues(
  selection: FilterSelection,
  key: string
): string[] {
  return selection[key] ?? [];
}

/**
 * Turns a selection into the clauses that make up the query sentence.
 *
 * Order comes from `facets`, not from the selection, so the sentence reads the same whichever
 * order the boxes were ticked in - a sentence that reshuffles itself as you click is harder to
 * re-read than one that holds still. Labels are resolved here so an unknown value (stale state,
 * an option that has since disappeared from the data) renders as its raw key instead of vanishing
 * silently from a sentence that claims to describe the whole query.
 */
export function describeFilterQuery(
  facets: FilterFacet[],
  selection: FilterSelection
): FilterClause[] {
  const clauses: FilterClause[] = [];
  for (const facet of facets) {
    const chosen = selectedValues(selection, facet.key);
    if (chosen.length === 0) continue;
    const labels = facet.options
      .filter((option) => chosen.includes(option.value))
      .map((option) => option.label);
    // Anything ticked that the facet no longer offers still has to appear, or the sentence
    // under-reports what is actually filtering the list.
    const orphans = chosen.filter(
      (value) => !facet.options.some((option) => option.value === value)
    );
    clauses.push({
      key: facet.key,
      phrase: facet.phrase,
      labels: [...labels, ...orphans],
    });
  }
  return clauses;
}
